import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimeTracker = ({ 
  activeOrder = null, 
  totalHoursToday = 0,
  onStartTimer,
  onPauseTimer,
  onStopTimer 
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (isActive && activeOrder) {
        setElapsedTime(prev => prev + 1);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, activeOrder]);

  useEffect(() => {
    setIsActive(activeOrder?.status === 'em_andamento');
    setElapsedTime(activeOrder?.elapsedTime || 0);
  }, [activeOrder]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
    }
    return `${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const calculateEarnings = (timeInSeconds, hourlyRate = 75) => {
    const hours = timeInSeconds / 3600;
    return hours * hourlyRate;
  };

  const formatCurrentTime = () => {
    return currentTime?.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrentDate = () => {
    return currentTime?.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional p-4 mb-4">
      {/* Current Time Display */}
      <div className="text-center mb-4">
        <div className="text-2xl font-mono font-bold text-card-foreground">
          {formatCurrentTime()}
        </div>
        <div className="text-sm text-muted-foreground capitalize">
          {formatCurrentDate()}
        </div>
      </div>
      {/* Active Timer */}
      {activeOrder ? (
        <div className="space-y-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-success animate-pulse' : 'bg-warning'}`}></div>
              <span className="text-sm font-medium text-muted-foreground">
                {isActive ? 'Trabalhando em' : 'Pausado em'}
              </span>
            </div>
            <h3 className="font-semibold text-card-foreground">
              OS #{activeOrder?.id}
            </h3>
            <p className="text-sm text-muted-foreground">
              {activeOrder?.clientName}
            </p>
          </div>

          {/* Timer Display */}
          <div className="bg-muted rounded-lg p-4 text-center">
            <div className="text-3xl font-mono font-bold text-card-foreground mb-2">
              {formatTime(elapsedTime)}
            </div>
            <div className="text-sm text-muted-foreground">
              Valor estimado: {formatCurrency(calculateEarnings(elapsedTime))}
            </div>
          </div>

          {/* Timer Controls */}
          <div className="grid grid-cols-2 gap-3">
            {isActive ? (
              <Button
                variant="warning"
                onClick={() => onPauseTimer(activeOrder)}
                className="touch-target"
                iconName="Pause"
                iconPosition="left"
              >
                Pausar
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => onStartTimer(activeOrder)}
                className="touch-target"
                iconName="Play"
                iconPosition="left"
              >
                Retomar
              </Button>
            )}
            
            <Button
              variant="destructive"
              onClick={() => onStopTimer(activeOrder)}
              className="touch-target"
              iconName="Square"
              iconPosition="left"
            >
              Parar
            </Button>
          </div>
        </div>
      ) : (
        /* No Active Timer */
        (<div className="text-center py-6">
          <Icon name="Clock" size={48} className="mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground mb-4">
            Nenhuma ordem em andamento
          </p>
          <p className="text-xs text-muted-foreground">
            Inicie uma ordem de serviço para começar o cronômetro
          </p>
        </div>)
      )}
      {/* Daily Summary */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Horas hoje:</span>
          <span className="font-medium text-card-foreground">
            {formatTime(totalHoursToday * 3600)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-muted-foreground">Valor do dia:</span>
          <span className="font-medium text-accent">
            {formatCurrency(calculateEarnings(totalHoursToday * 3600))}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TimeTracker;